<template>
    <div class="person card" :class="isOk ? 'person-ok' : 'person-error'">
        <div class="card-header">Person #{{ personNumber }}</div>
        <div class="card-body">
            <div class="form-row">
                <div class="form-group col-md-4">
                    <label>Type</label>
                    <div class="row">
                        <button-radio-container
                                :disabledMessage="typeDisabledMessage"
                                :options="types"
                                v-model="value.type"
                                :disabled="!canSetType"
                        />
                    </div>
                </div>
                <div class="form-group col-md-4" v-if="value.type">
                    <label>Age (<span style="font-style: italic">{{ internalAge }}</span>)</label>
                    <vue-slider
                            v-model="internalAge"
                            :min="ageMin"
                            :max="ageMax"
                    />
                </div>
                <div class="form-group col-md-4">
                    <label>Has mobile</label>
                    <div class="row">
                        <button-radio-container
                                :options="hasMobile"
                                cast="integer"
                                v-model="value.hasMobile"
                        />
                    </div>
                </div>
            </div>
            <div class="form-row">
                <div class="form-group col-md-4">
                    <label>Gender</label>
                    <div class="row">
                        <button-radio-container
                                :options="genders"
                                v-model="value.gender"
                        />
                    </div>
                </div>
                <div class="form-group col-md-4">
                    <label>Ethnicity</label>
                    <div class="row">
                        <button-radio-container
                                :options="ethnicities"
                                cast="integer"
                                v-model="value.ethnicity"
                        />
                    </div>
                </div>
                <div class="form-group col-md-4">
                    <label>Role</label>
                    <div class="row">
                        <button-radio-container
                                disabledMessage="Only L3 sessions require the role to be set"
                                :options="roles"
                                v-model="value.role"
                                :disabled="!canSetRole"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import ButtonRadioContainer from "@/components/common/ButtonRadioContainer";
    import VueSlider from 'vue-slider-component'
    import 'vue-slider-component/theme/antd.css'
    import Vue from 'vue';
    import * as MutationTypes from '@/store/mutation-types';

    export default {
        name: 'people-selector-single',
        components: {
            ButtonRadioContainer,
            VueSlider
        },
        props: {
            session: {
                type: Object
            },
            value: {
                type: Object,
                default() {
                    return {};
                }
            },
            personNumber: {
                type: Number
            }
        },
        mounted() {
            if (this.personNumber === 1) {
                Vue.set(this.value, 'type', 'child');
            } else if (this.personNumber === 2) {
                // If they want person #2 to not be forced to be an adult, remove this line again
                Vue.set(this.value, 'type', 'adult');
            }

            if (!this.value || typeof(this.value.hasMobile) === 'undefined') {
                Vue.set(this.value, 'hasMobile', 1);
            } else if (this.value.hasMobile) {
                Vue.set(this.value, 'hasMobile', 1);
            } else {
                Vue.set(this.value, 'hasMobile', 0);
            }

            if (this.value) {
                if (!this.internalAge && this.value.age) {
                    this.internalAge = this.value.age;
                }
            }

            console.log('value', this.value);

            this.updateStore();
        },
        methods: {
            updateStore() {
                if (this.value.type === 'child') {
                    this.$store.commit(MutationTypes.SET_SCENARIO_ACTIVE_CHILD, {
                        age: this.value.age,
                        gender: this.value.gender
                    });
                } else {
                    this.$store.commit(MutationTypes.SET_SCENARIO_ACTIVE_ADULT, {
                        age: this.value.age,
                        gender: this.value.gender
                    });
                }
            }
        },
        watch: {
            value: {
                deep: true,
                handler() {
                    this.updateStore();
                }
            },
            internalAge(internalAge) {
                Vue.set(this.value, 'age', internalAge);
            },
            ageMin(ageMin) {
                if (!this.internalAge) {
                    this.internalAge = ageMin;
                }

                this.internalAge = Math.max(this.internalAge, ageMin);
            },
            ageMax(ageMax) {
                if (!this.internalAge) {
                    this.internalAge = ageMax;
                }

                this.internalAge = Math.min(this.internalAge, ageMax);
            }
        },
        computed: {
            typeDisabledMessage() {
                if (this.personNumber === 1) {
                    return 'The first person is required to always be a child';
                } else if (this.personNumber === 2) {
                    return 'The second person is required to always be an adult';
                }
            },
            canSetType() {
                return this.personNumber > 2;
            },
            canSetRole() {
                return this.session && this.session.difficultyLevel === 'L3';
            },
            ageMin() {
                if (this.value.type === 'adult') {
                    return 18;
                } else if (this.value.type === 'child') {
                    return 8;
                }

                return null;
            },
            ageMax() {
                if (this.value.type === 'adult') {
                    return 100;
                } else if (this.value.type === 'child') {
                    return 17;
                }

                return null;
            },
            isOk() {
                if (this.canSetType && !this.value.type) {
                    return false;
                }

                if (!this.value.age) {
                    return false;
                }

                if (!this.value.gender) {
                    return false;
                }

                // Ethnicity "0" is valid - it's the first one
                if (!(this.value.ethnicity === 0 ||this.value.ethnicity === 1 || this.value.ethnicity === 2)) {
                    return false;
                }

                if (this.canSetRole && !this.value.role) {
                    return false;
                }

                return true;
            }
        },
        data() {
            return {
                types: {
                    'child': 'Child',
                    'adult': 'Adult',
                },

                genders: {
                    'male': 'Male',
                    'female': 'Female',
                },

                ethnicities: {
                    '0': 'Caucasian',
                    '1': 'Black',
                    '2': 'Middle eastern',
                },

                roles: {
                    'victim': 'Victim',
                    'perpetrator': 'Perpetrator',
                },

                hasMobile: {
                    '1': 'Yes',
                    '0': 'No',
                },

                internalAge: null
            }
        }
    }
</script>

<style lang="scss" scoped>
    .person {
        &.person-ok {

        }

        &.person-error {
            background: #ffbcbcbc;
        }
    }
</style>
